import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import groupService from '../services/groupService';

export const useGroupOperations = () => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeBasicInfoProcess, setActiveBasicInfoProcess] = useState(null);
  const [activeAIAnalysisProcess, setActiveAIAnalysisProcess] = useState(null);
  const [activeAccountScannerProcess, setActiveAccountScannerProcess] = useState(null);
  const [activeJoinProcess, setActiveJoinProcess] = useState(null);
  const [activeUnsubscribeProcess, setActiveUnsubscribeProcess] = useState(null);

  const queryClient = useQueryClient();

  // Clear messages
  const clearError = () => setError(null);
  const clearSuccess = () => setSuccess(null);

  // Mutations for bulk operations
  const batchAnalyzeMutation = useMutation(
    (data) => groupService.batchAnalyze(data),
    {
      onSuccess: () => {
        setSuccess('Batch analysis started successfully');
        queryClient.invalidateQueries('groups');
      },
      onError: (err) => {
        setError(`Batch analysis failed: ${err.message}`);
      }
    }
  );

  const unsubscribeMutation = useMutation(
    (chatId) => groupService.unsubscribeFromGroup(chatId),
    {
      onSuccess: () => {
        setSuccess('Successfully unsubscribed from group');
        queryClient.invalidateQueries('groups');
      },
      onError: (err) => {
        setError(`Failed to unsubscribe: ${err.message}`);
      }
    }
  );

  const batchUnsubscribeMutation = useMutation(
    (data) => groupService.batchUnsubscribe(data),
    {
      onSuccess: () => {
        setSuccess('Batch unsubscribe started successfully');
        queryClient.invalidateQueries('groups');
      },
      onError: (err) => {
        setError(`Batch unsubscribe failed: ${err.message}`);
      }
    }
  );

  const accountScanMutation = useMutation(
    (data) => groupService.scanAccounts(data),
    {
      onSuccess: () => {
        setSuccess('Account scanning started successfully');
        queryClient.invalidateQueries('groups');
      },
      onError: (err) => {
        setError(`Account scan failed: ${err.message}`);
      }
    }
  );

  // Operation handlers
  const handleBatchAnalysis = async (selectedGroups, massAnalysisSettings) => {
    if (selectedGroups.size === 0) {
      setError('Please select groups to analyze');
      return;
    }

    try {
      const selectedArray = Array.from(selectedGroups);
      const analysisOptions = {
        maxConcurrent: massAnalysisSettings.maxConcurrent,
        delayBetween: massAnalysisSettings.delayBetween
      };

      if (massAnalysisSettings.openaiModel) analysisOptions.openaiModel = massAnalysisSettings.openaiModel;
      if (massAnalysisSettings.escalationModel) analysisOptions.escalationModel = massAnalysisSettings.escalationModel;
      if (typeof massAnalysisSettings.enableEscalation === 'boolean') {
        analysisOptions.enableEscalation = massAnalysisSettings.enableEscalation;
      }
      if (massAnalysisSettings.escalationValidationThreshold) {
        analysisOptions.escalationValidationThreshold = massAnalysisSettings.escalationValidationThreshold;
      }
      if (massAnalysisSettings.escalationWarningThreshold) {
        analysisOptions.escalationWarningThreshold = massAnalysisSettings.escalationWarningThreshold;
      }

      const result = await groupService.startAIAnalysis(selectedArray, analysisOptions);

      if (result.processId) {
        setSuccess('AI analysis started successfully');
        setActiveAIAnalysisProcess(result.processId);

        // Refresh groups after a delay
        setTimeout(() => {
          queryClient.invalidateQueries('groups');
        }, 2000);

        return { success: true, processId: result.processId };
      } else {
        setError('Failed to start AI analysis');
        return { success: false };
      }
    } catch (err) {
      console.error('AI analysis error:', err);
      setError(`AI analysis failed: ${err.message}`);
      return { success: false };
    }
  };

  const handleAccountScan = async () => {
    try {
      const result = await groupService.scanAccounts({
        maxConcurrent: 2,
        delayBetween: 5000,
        skipRecentlyScanned: true,
        maxHoursSinceLastScan: 24
      });

      if (result.processId) {
        setSuccess('Account scan started successfully');
        setActiveAccountScannerProcess(result.processId);
        
        // Refresh groups after a delay
        setTimeout(() => {
          queryClient.invalidateQueries('groups');
        }, 2000);

        return { success: true, processId: result.processId };
      } else {
        setError('Failed to start account scan');
        return { success: false };
      }
    } catch (err) {
      console.error('Account scan error:', err);
      setError(`Account scan failed: ${err.message}`);
      return { success: false };
    }
  };

  const handleGetBasicInfo = async (selectedGroups, basicInfoSettings) => {
    if (selectedGroups.size === 0) {
      setError('Please select groups first');
      return;
    }

    try {
      const selectedArray = Array.from(selectedGroups);
      const result = await groupService.batchGetBasicInfo({
        chatIds: selectedArray,
        maxGroups: selectedArray.length,
        ...basicInfoSettings
      });

      if (result.processId) {
        setSuccess(`Basic info collection started for ${result.queued} groups`);
        setActiveBasicInfoProcess(result.processId);

        // Refresh groups after a delay to show updated statuses
        setTimeout(() => {
          queryClient.invalidateQueries('groups');
        }, 2000);

        return { success: true, processId: result.processId };
      } else {
        setError('Failed to start basic info collection');
        return { success: false };
      }
    } catch (err) {
      console.error('Basic info collection error:', err);
      setError(`Basic info collection failed: ${err.message}`);
      return { success: false };
    }
  };

  const handleUnsubscribe = (chatId) => {
    unsubscribeMutation.mutate(chatId);
  };

  const handleJoinGroups = async (selectedGroups, joinSettings = {}) => {
    if (selectedGroups.size === 0) {
      setError('Please select groups to join');
      return;
    }

    try {
      const selectedArray = Array.from(selectedGroups);
      const result = await groupService.batchJoinGroups({
        chatIds: selectedArray,
        maxConcurrent: joinSettings.maxConcurrent || 1,
        delayBetween: joinSettings.delayBetween || 3000,
        maxRetries: joinSettings.maxRetries || 3
      });

      if (result.processId) {
        setSuccess(`Group joining started for ${result.queued} groups`);
        setActiveJoinProcess(result.processId);

        // Refresh groups after a delay to show updated statuses
        setTimeout(() => {
          queryClient.invalidateQueries('groups');
        }, 2000);

        return { success: true, processId: result.processId };
      } else {
        setError('Failed to start group joining');
        return { success: false };
      }
    } catch (err) {
      console.error('Group joining error:', err);
      setError(`Group joining failed: ${err.message}`);
      return { success: false };
    }
  };

  const handleBatchUnsubscribe = async (selectedGroups, unsubscribeSettings = {}) => {
    if (selectedGroups.size === 0) {
      setError('Please select groups to unsubscribe from');
      return;
    }

    try {
      const selectedArray = Array.from(selectedGroups);
      const result = await groupService.batchUnsubscribe({
        chatIds: selectedArray,
        delayBetween: unsubscribeSettings.delayBetween || 1000
      });

      if (result.processId) {
        setSuccess(`Batch unsubscribe started for ${result.queued} groups`);
        setActiveUnsubscribeProcess(result.processId);

        // Refresh groups after a delay to show updated statuses
        setTimeout(() => {
          queryClient.invalidateQueries('groups');
        }, 2000);

        return { success: true, processId: result.processId };
      } else {
        setError('Failed to start batch unsubscribe');
        return { success: false };
      }
    } catch (err) {
      console.error('Batch unsubscribe error:', err);
      setError(`Batch unsubscribe failed: ${err.message}`);
      return { success: false };
    }
  };


  return {
    // State
    error,
    success,
    activeBasicInfoProcess,
    activeAIAnalysisProcess,
    activeAccountScannerProcess,
    activeJoinProcess,
    activeUnsubscribeProcess,

    // Actions
    clearError,
    clearSuccess,
    setActiveBasicInfoProcess,
    setActiveAIAnalysisProcess,
    setActiveAccountScannerProcess,
    setActiveJoinProcess,
    setActiveUnsubscribeProcess,

    // Handlers
    handleBatchAnalysis,
    handleAccountScan,
    handleGetBasicInfo,
    handleUnsubscribe,
    handleJoinGroups,
    handleBatchUnsubscribe,

    // Mutations
    batchAnalyzeMutation,
    unsubscribeMutation,
    batchUnsubscribeMutation,
    accountScanMutation,

    // Loading states
    isLoading: {
      batchAnalysis: batchAnalyzeMutation.isLoading,
      unsubscribe: unsubscribeMutation.isLoading,
      batchUnsubscribe: batchUnsubscribeMutation.isLoading,
      accountScan: accountScanMutation.isLoading,
      joinGroups: false // We'll use process status for this
    }
  };
};
